<template>
  <div class="min-height">
    <TableLoading v-if="orders.page === 0"/>
    <div v-else>
      <form @submit.prevent="createCreditNote">
        <div class="row">
          <div class="col-sm-12">
            <div class="mb-3">
              <label>Order</label>
              <multiselect v-model="credit.order" :options="orders.objects" :class="{
                        'is-invalid': v$.credit.order.$error,
                  }" @input="selectOrder" class="multiselect-height-3" :disabled="isDisabled"
                           :custom-label="ordersLabel" placeholder="Select one" label="invoiceId" track-by="invoiceId" :searchable="true" @search-change="asyncFindOrder">
              </multiselect>
              <p
                  v-for="error of v$.credit.order.$errors"
                  :key="error.$uid"
                  class="invalid-feedback"
              >
                {{error.$message}}
              </p>
            </div>
          </div>
          <div class="row my-3"  v-for="(p, r) in credit.items"
               :key="r">
            <div class="mb-3 col-sm-6">
              <label>Item</label>
              <multiselect v-model="credit.items[r].item" :options="orderItems" class="multiselect-height-3"
                           placeholder="Select one" label="item" track-by="item" :searchable="true">
              </multiselect>
            </div>
            <div class="mb-3 col-sm-2">
              <label>Pieces</label>
              <input
                  v-model="credit.items[r].itemPieces"
                  type="number"
                  :max="credit.items[r].item.pieces"
                  min="0"
                  class="form-control py-3"
              />
            </div>
            <div class="mb-3 col-sm-2">
              <label>Order Units</label>
              <input
                  v-model="credit.items[r].itemCount"
                  type="number"
                  :max="credit.items[r].item.count"
                  min="0"
                  class="form-control py-3"
              />
            </div>
            <div class="mb-3 col-sm-2 d-flex form-group align-self-end">
              <button
                  class="btn rounded-circle btn-dark mx-2"
                  v-if="r > 0"
                  @click.prevent="remove(credit.items, r)"
              >
                <i class="bx bx-minus"></i>
              </button>
              <button
                  class="btn rounded-circle btn-primary mx-2"
                  v-if="credit.items.length < orderItems.length && r == credit.items.length - 1"
                  @click.prevent="add(credit.items)"
              >
                <i class="bx bx-plus"></i>
              </button>
            </div>
          </div>
          <div class="col-sm-12">
            <div class="mb-3">
              <label>Remarks</label>
              <textarea
                  v-model="credit.remarks"
                  name="name"
                  rows="5"
                  type="text"
                  class="form-control py-3"
                  :class="{
                        'is-invalid': v$.credit.remarks.$error,
                      }"
              ></textarea>
              <p
                  v-for="error of v$.credit.remarks.$errors"
                  :key="error.$uid"
                  class="invalid-feedback"
              >
                {{error.$message}}
              </p>
            </div>
          </div>
          <div v-for="(error, index) in ordersErrors" :key="index" style="color: #D13704;">
            {{error.message}}
          </div>
          <div class="mt-3 align-middle">
            <b-button variant="primary" size="lg" class="py-3 h3 w-100" disabled v-if="ordersLoading">
              <i class="bx bx-loader bx-spin font-size-16 align-middle me-2"></i>
              Loading
            </b-button>
            <b-button variant="primary" size="lg" class="py-3 h3 text-white w-100" type="submit" v-else>
              Generate Credit Note
            </b-button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import TableLoading from "@/components/TableLoading";
import {ALL_CUSTOMER_ORDERS} from "@/gql/orders";
import {required} from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import Multiselect from "vue-multiselect";
import {mapGetters} from "vuex";
export default {
  name: "credit-note-generation-component",
  setup () {
    return { v$: useVuelidate() }
  },
  components: {TableLoading, Multiselect},
  data(){
    return {
      orders: {
        page: 0,
        objects: []
      },
      orderSearchKeyword: '',
      orderItems: [],
      credit:{
        order: this.selectedOrder,
        remarks: "",
        items: this.selectOrder.orderItems
      }
    }
  },
  props: {
    selectedOrder: {
      default(){
        return {
          publicId: '',
          customer: {
            name: ''
          },
          invoiceId: '',
          orderItems: []
        }
      },
      type: Object
    },
    isDisabled: {
      default: false,
      type: Boolean
    }
  },
  apollo: {
    orders: {
      query: ALL_CUSTOMER_ORDERS,
      variables(){
        return {
          page: 1,
          perPage: 30,
          searchKeyword: this.orderSearchKeyword,
          orderStatus: ["601fdbd6-c437-4758-98a2-e807e847f566"],
        }
      }
    }
  },
  validations () {
    return {
      credit:{
        order: {required},
        remarks: {required},
      }
    }
  },
  computed: {
    ... mapGetters({
      ordersLoading: 'orders/ordersLoading',
      ordersErrors: 'orders/ordersErrors'
    }),
  },
  methods: {
    async createCreditNote() {
      const isFormCorrect = await this.v$.$validate()
      if (!isFormCorrect) {
        return
      }

      const new_items = []
      this.credit.items.forEach((item) => {
        new_items.push({'itemId': item.item.itemId, 'itemCount': parseInt(item.itemCount), 'itemPieces': parseInt(item.itemPieces)})
      })

      const data = {
        "orderId": this.credit.order.publicId,
        "remarks": this.credit.remarks,
        "items": new_items
      }
      this.$emit('creditAction', data)
      this.v$.$touch();
    },
    asyncFindOrder (query) {
      this.orderSearchKeyword = query
    },
    ordersLabel ({ invoiceId, customer }) {
      return `#${invoiceId} — ${customer.name}`
    },
    selectOrder(value){
      const items = value.orderItems
      const result = []
      items.forEach((item)=>{
        result.push({
          "itemId": item.publicId,
          "item": item.item.productName,
          "count": item.itemCount,
          "pieces": item.itemPieces,
        })
      })
      this.credit.items = [{
        item:{
          count:0,
          pieces: 0
        },
        itemCount: 0,
        itemPieces: 0
      }]
      this.orderItems = result
    },
    remove(p, i) {
      p.splice(i, 1);
    },
    add(p) {
      p.push({
        item:{
          count:0,
          pieces: 0
        },
        itemCount: 0,
        itemPieces: 0
      });
    },
  },
  mounted() {
    if (this.selectedOrder.orderItems.length > 0){
      this.selectOrder(this.selectedOrder)
    }
  }
}
</script>

<style scoped>

</style>
